import { Assembly } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { ComponentCard, ComponentCardSkeleton } from '../ComponentCard';

export const useComponentsListData = ({
  components,
  totalComponents,
}: {
  components: Partial<Assembly>[];
  totalComponents: number;
}) => {
  const selectedViewerComponentId = useAtomValue(state.selectedAssemblyInfo);
  const viewerComponents = useAtomValue(state.filteredAssemblies);
  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemSize, setItemSize] = useState(0);
  const [isAtScrollStart, setIsAtScrollStart] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<FixedSizeList>(null);

  const isSelectedComponentExistOnPointCloud = useMemo(() => {
    if (
      selectedViewerComponentId &&
      viewerComponents?.find((component) => component.id === selectedViewerComponentId)
    ) {
      return true;
    }
    return false;
  }, [selectedViewerComponentId, viewerComponents]);

  const highlightedComponentCardInfo = useMemo(() => {
    return isSelectedComponentExistOnPointCloud
      ? viewerComponents?.find((component) => component.id === selectedViewerComponentId)
      : undefined;
  }, [viewerComponents, isSelectedComponentExistOnPointCloud, selectedViewerComponentId]);

  const displayComponents = useMemo(() => {
    if (!isSelectedComponentExistOnPointCloud) {
      return components;
    }

    // Filter out the highlighted component to avoid duplication
    return components.filter((component) => component.id !== selectedViewerComponentId);
  }, [components, isSelectedComponentExistOnPointCloud, selectedViewerComponentId]);

  useEffect(() => {
    const currentContainer = containerRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (currentContainer?.offsetHeight) setContainerHeight(currentContainer.offsetHeight);
      if (currentContainer?.offsetWidth) setContainerWidth(currentContainer.offsetWidth);
      const rootFontSize = getComputedStyle(document.documentElement);
      const parsed = parseFloat(rootFontSize.fontSize);
      setItemSize(7.6 * parsed);
    });

    if (currentContainer) resizeObserver.observe(currentContainer);

    return () => {
      if (currentContainer) resizeObserver.unobserve(currentContainer);
    };
  }, [containerRef]);

  const isItemLoaded = (index: number) => {
    // The highlighted component at index 0 is always loaded
    if (highlightedComponentCardInfo && index === 0) return true;

    // Adjust index for the rest of the items
    const adjustedIndex = isSelectedComponentExistOnPointCloud ? index - 1 : index;

    // Check if the item at the adjusted index is loaded
    return (
      adjustedIndex <
      (isSelectedComponentExistOnPointCloud ? displayComponents.length : components.length)
    );
  };

  const Row = ({ index, style }: ListChildComponentProps) => (
    <div style={style}>
      {/* Show highlighted component at the top */}
      {highlightedComponentCardInfo && index === 0 ? (
        <ComponentCard component={highlightedComponentCardInfo} />
      ) : index !==
        (isSelectedComponentExistOnPointCloud
          ? displayComponents.length + 1
          : components.length) ? (
        <ComponentCard
          component={
            isSelectedComponentExistOnPointCloud
              ? (displayComponents[index - 1] ?? {})
              : (components[index] ?? {})
          }
        />
      ) : (
        <ComponentCardSkeleton />
      )}
    </div>
  );

  const hasNextPage = useMemo(() => {
    return components.length < totalComponents;
  }, [components.length, totalComponents]);

  const itemCount = useMemo(() => {
    if (components.length === 0 && totalComponents === 0) {
      return 0;
    }
    // Base count is the number of components plus a loading indicator if there are more to load
    const baseCount = hasNextPage
      ? (isSelectedComponentExistOnPointCloud ? displayComponents.length : components.length) + 1
      : isSelectedComponentExistOnPointCloud
        ? displayComponents.length
        : components.length;

    return baseCount;
  }, [
    components.length,
    displayComponents.length,
    hasNextPage,
    isSelectedComponentExistOnPointCloud,
    totalComponents,
  ]);

  const handleScrollToTop = () => {
    if (listRef.current) {
      listRef.current.scrollToItem(0, 'auto');
    }
  };

  const handleScroll = useCallback(({ scrollOffset }: { scrollOffset: number }) => {
    setIsAtScrollStart(scrollOffset === 0);
  }, []);

  useEffect(() => {
    if (highlightedComponentCardInfo && listRef.current) {
      listRef.current.scrollToItem(0, 'auto');
    }
  }, [highlightedComponentCardInfo]);

  return {
    containerRef,
    listRef,
    isItemLoaded,
    itemCount,
    Row,
    handleScrollToTop,
    handleScroll,
    isAtScrollStart,
    containerHeight,
    containerWidth,
    itemSize,
  };
};
